import React, { useState, useEffect, useRef } from 'react';

interface Stat {
  value: string;
  suffix: string;
  label: string;
  icon: string;
  color: string;
}

const OutcomesSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats: Stat[] = [
    {
      value: '70',
      suffix: '%',
      label: 'Save on software costs',
      icon: '💰',
      color: 'from-green-500 to-emerald-500',
    },
    {
      value: '10',
      suffix: '+',
      label: 'Subscriptions replaced',
      icon: '🔄',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      value: '80',
      suffix: '%',
      label: 'Admin tasks automated',
      icon: '⚡',
      color: 'from-purple-500 to-pink-500',
    },
    {
      value: '10',
      suffix: '+',
      label: 'Hours saved weekly',
      icon: '⏰',
      color: 'from-orange-500 to-red-500',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 mb-4 text-sm font-semibold text-indigo-600 bg-indigo-50 rounded-full">
            Real Business Impact
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
            What Businesses Achieve
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              With Explanatum
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join thousands of businesses transforming their operations with one interconnected AI platform
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className={`group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-blue-200 ${
                isVisible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{
                animationDelay: `${index * 100}ms`,
              }}
            >
              {/* Icon */}
              <div className={`inline-flex items-center justify-center w-16 h-16 mb-4 rounded-xl bg-gradient-to-br ${stat.color} text-white text-3xl shadow-md group-hover:scale-110 transition-transform duration-300`}>
                {stat.icon}
              </div>

              {/* Animated Number */}
              <div className="mb-2">
                <span className={`text-5xl font-extrabold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                  {isVisible ? stat.value : '0'}
                  <span className="text-4xl">{stat.suffix}</span>
                </span>
              </div>

              {/* Label */}
              <p className="text-lg font-semibold text-gray-700">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Additional Benefits */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-2 border-blue-100">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
              Why Businesses Choose Explanatum
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  icon: '🚀',
                  title: 'Faster Growth',
                  description: 'AI insights help you spot opportunities and act on them faster than competitors',
                },
                {
                  icon: '💡',
                  title: 'Smarter Decisions',
                  description: 'Data-driven recommendations across every business function in real-time',
                },
                {
                  icon: '🔗',
                  title: 'Seamless Integration',
                  description: 'All tools work together — no more data silos or manual data entry',
                },
                {
                  icon: '📈',
                  title: 'Scalable Platform',
                  description: 'Grow from idea to enterprise without switching tools or losing momentum',
                },
              ].map((benefit, index) => (
                <div key={benefit.title} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-2xl">
                      {benefit.icon}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">{benefit.title}</h4>
                    <p className="text-gray-600 text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default OutcomesSection;
